import React from "react";
import { Dialog, Box, Typography, Button, CircularProgress, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LinkIcon from "@mui/icons-material/Link";
import NotesIcon from "@mui/icons-material/Notes";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CodeIcon from "@mui/icons-material/Code";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SortIcon from "@mui/icons-material/Sort";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import { GOLD_THEME, GOLD_BTN_SX } from "./goldTheme";

// ═════════════════════════════════════════════════════════════════════
//  The app's ONE form-dialog design (create / edit / view) — taken from "Add New Module":
//    · cream rounded dialog, soft blur backdrop
//    · header: gold logo tile + bold title + muted subtitle, "MarketSync · Admin Panel" badge in the top-right corner
//    · body: labelled fields — each label has a small gold line icon (FieldLabel) — built from <GoldField/>
//    · footer: Cancel (cream, outlined) + the main action (dark, gold text) — <GoldFormBtn/>
//
//    <GoldFormDialog open onClose title subtitle width footer={<>…<GoldFormBtn/>…</>}> …fields… </GoldFormDialog>
//    <FieldLabel>Module Name *</FieldLabel>            (icon picked from the label's words, or pass icon={Icon})
//    <GoldFormBtn variant="secondary" icon={<CloseIcon/>} onClick>Cancel</GoldFormBtn>
//    <GoldFormBtn variant="primary" loading disabled onClick>Create Module</GoldFormBtn>
// ═════════════════════════════════════════════════════════════════════

const LABEL_ICONS = [
  [/email|mail/i, EmailOutlinedIcon],
  [/password/i, LockOutlinedIcon],
  [/route|path|url|link/i, LinkIcon],
  [/description|note|feature/i, NotesIcon],
  [/code/i, CodeIcon],
  [/role|permission|access/i, ShieldOutlinedIcon],
  [/group/i, GroupsOutlinedIcon],
  [/price|amount|cost|plan|subscription/i, PaymentsOutlinedIcon],
  [/duration|month|schedule|date|time/i, ScheduleIcon],
  [/parent|module/i, AccountTreeOutlinedIcon],
  [/store|compan/i, StorefrontOutlinedIcon],
  [/order|sort|position/i, SortIcon],
  [/icon|image|logo|photo/i, ImageOutlinedIcon],
  [/status|active|enable/i, ToggleOnOutlinedIcon],
  [/name|title|label/i, BadgeOutlinedIcon],
];
const iconFor = (text) => {
  const t = String(text || "");
  const hit = LABEL_ICONS.find(([re]) => re.test(t));
  return hit ? hit[1] : ListAltIcon;
};
const plainText = (children) => React.Children.toArray(children).map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : "")).join(" ");

export const FieldLabel = ({ children, icon, palette, sx }) => { // `palette` is accepted (older call sites) and ignored
  const Icon = icon || iconFor(plainText(children));
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.9, mt: 1.4, ...sx }}>
      <Icon sx={{ fontSize: 17, color: GOLD_THEME.gold, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: GOLD_THEME.text, lineHeight: 1.2 }}>{children}</Typography>
    </Box>
  );
};

export const GoldFormBtn = ({ variant = "secondary", icon, loading = false, children, sx, ...rest }) => {
  const primary = variant === "primary";
  const danger = variant === "danger";
  return (
    <Button {...rest} disabled={rest.disabled || loading}
      startIcon={loading ? null : icon}
      sx={{
        flex: primary || danger ? "1.4 1 0" : "1 1 0", minWidth: 0, textTransform: "none", fontWeight: 700, fontSize: "0.92rem",
        py: 1.15, px: 2, borderRadius: "14px", whiteSpace: "nowrap",
        ...(primary ? {
          // the same brown-gold button every page uses (GOLD_BTN_SX in goldTheme.jsx)
          background: GOLD_BTN_SX.background, color: GOLD_BTN_SX.color, border: "none",
          boxShadow: GOLD_BTN_SX.boxShadow,
          "&:hover": GOLD_BTN_SX["&:hover"],
          "&.Mui-disabled": GOLD_BTN_SX["&.Mui-disabled"],
        } : danger ? {
          background: "linear-gradient(180deg, #dc2626, #b91c1c)", color: "#fff", border: "1.5px solid #b91c1c",
          boxShadow: "0 8px 20px rgba(185,28,28,0.3)",
          "&:hover": { background: "linear-gradient(180deg, #e23a3a, #c42020)" },
        } : {
          background: "#fffaf0", color: "#7a5712", border: "1.5px solid #d9c79e",
          "&:hover": { background: "#fdf1d8", borderColor: "#b8892c" },
          "&.Mui-disabled": { opacity: 0.55 },
        }),
        ...sx,
      }}>
      {loading ? <CircularProgress size={20} sx={{ color: primary || danger ? "#fff" : "#7a5712" }} /> : children}
    </Button>
  );
};

export const CancelIcon = CloseIcon;
export const PlusIcon = AddIcon;
export const OkIcon = CheckIcon;

export default function GoldFormDialog({ open, onClose, title, subtitle, width = 680, badge = "Admin Panel", children, footer, zIndex }) {
  return (
    <Dialog
      open={!!open} onClose={onClose} TransitionComponent={Fade} maxWidth={false} sx={zIndex ? { zIndex } : undefined}
      BackdropProps={{ sx: { backdropFilter: "blur(14px)", background: "rgba(184,137,44,0.18)" } }}
      PaperProps={{
        sx: {
          width: { xs: "94vw", sm: width }, maxWidth: "94vw", maxHeight: "92vh", m: 1.5,
          display: "flex", flexDirection: "column", overflow: "hidden", position: "relative",
          borderRadius: "24px", border: "1px solid #ece2cc", color: GOLD_THEME.text,
          background: "linear-gradient(180deg, #fffcf7 0%, #f9f2e3 100%)",
          boxShadow: "0 30px 80px rgba(60,40,8,0.28), 0 1px 0 rgba(255,255,255,0.9) inset",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.6, pb: 1.6, display: "flex", alignItems: "center", gap: 1.8, position: "relative", flexShrink: 0 }}>
        <Box sx={{
          width: 52, height: 52, borderRadius: "16px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(145deg, #f1d58b, #c99a35)", boxShadow: "0 8px 20px rgba(111,82,18,0.30), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}>
          <Box component="img" src="/assests/website-logo.png" alt="" sx={{ width: 30, height: "auto", objectFit: "contain" }} />
        </Box>
        <Box sx={{ minWidth: 0, pr: { xs: 0, sm: 12 } }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.25, color: GOLD_THEME.text }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: "0.8rem", color: GOLD_THEME.muted, mt: 0.2 }}>{subtitle}</Typography>}
        </Box>
        {badge && (
          <Box sx={{
            display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.7, position: "absolute", top: 0, right: 0,
            pl: 2.4, pr: 2.2, pt: 1, pb: 1.2, borderBottomLeftRadius: "22px", background: "linear-gradient(180deg, #fff8e8, #f5e7c3)",
            borderLeft: "1px solid #ecd9ad", borderBottom: "1px solid #ecd9ad",
          }}>
            <Box component="img" src="/assests/website-logo.png" alt="" sx={{ width: 18, height: "auto", objectFit: "contain" }} />
            <Box sx={{ lineHeight: 1 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#7a5712", lineHeight: 1.1 }}>MarketSync</Typography>
              <Typography sx={{ fontSize: "0.55rem", color: GOLD_THEME.muted, lineHeight: 1.1 }}>{badge}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box sx={{
        px: 3, py: 1, flex: "1 1 auto", overflowY: "auto", minHeight: 0,
        "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#e3d6b8", borderRadius: "4px" },
        "& .MuiOutlinedInput-root.Mui-focused::before": { display: "none !important" },
      }}>
        {children}
      </Box>

      {/* Footer */}
      {footer && (
        <Box sx={{ px: 3, pb: 3, pt: 2, display: "flex", gap: 1.6, flexShrink: 0 }}>{footer}</Box>
      )}
    </Dialog>
  );
}
